import { ApiError } from '../../abstract/api-error';
import { TestQuestionOption } from '../data-models/test-question-option.model';
import { QuestionImportPreview } from '../response-models/question-import-preview.model';
import { QuestionDifficulty } from '../response-models/question-difficulty.model';
import { QuestionImportRow } from '../response-models/question-import-row.model';
import { TestAnalyticsSummary } from '../response-models/test-analytics-summary.model';
import { TestAnalytics } from '../response-models/test-analytics.model';
import { TestMetrics } from '../response-models/test-metrics.model';
import { isRecord, recordOrEmpty } from './is-record';
import { countOrZero, idString, listOf, nullableString, stringArrayOrEmpty } from './wire-value-utils';

/** The hub's error code when a question import CSV has no valid row (`detail.questions` holds the parsed rows). */
export const NO_VALID_QUESTIONS = 'NO_VALID_QUESTIONS';

/** Readers for the safety-test responses (hub `admin-safety-tests-controller.ts`) that are not entities. */

/** An answer option of a question (test detail, admin view). */
export function toTestQuestionOption(value: unknown): TestQuestionOption {
  const source = recordOrEmpty(value);
  return { id: idString(source['id']), text: nullableString(source['text']) ?? '', isCorrect: source['isCorrect'] === true };
}

/** One parsed row of a question import CSV. */
export function toQuestionImportRow(value: unknown): QuestionImportRow {
  const source = recordOrEmpty(value);
  return {
    text: nullableString(source['text']) ?? '',
    type: nullableString(source['type']) ?? '',
    explanation: nullableString(source['explanation']) ?? null,
    options: listOf(source['options'], toImportOption),
    isValid: source['isValid'] === true,
    errors: stringArrayOrEmpty(source['errors']),
  };
}

/** A question import preview, or `null` when the hub sent none. */
export function toQuestionImportPreview(value: unknown): QuestionImportPreview | null {
  if (!isRecord(value)) {
    return null;
  }
  return {
    totalRows: countOrZero(value['totalRows']),
    validRows: countOrZero(value['validRows']),
    invalidRows: countOrZero(value['invalidRows']),
    questions: listOf(value['questions'], toQuestionImportRow),
  };
}

function toImportOption(value: unknown): { text: string; isCorrect: boolean } {
  const source = recordOrEmpty(value);
  return { text: nullableString(source['text']) ?? '', isCorrect: source['isCorrect'] === true };
}

/**
 * The parsed rows from a `NO_VALID_QUESTIONS` import failure (`ApiError.detail.questions`), so the page can show
 * what to fix; `null` for any other error.
 */
export function noValidQuestionRows(error: unknown): QuestionImportRow[] | null {
  const apiError = ApiError.fromHttpError(error);
  if (apiError.code !== NO_VALID_QUESTIONS || !isRecord(apiError.detail)) {
    return null;
  }
  return listOf(apiError.detail['questions'], toQuestionImportRow);
}

/** `GET /admin/safety-tests/analytics` (display totals, so missing counts read as 0). */
export function toTestAnalytics(value: unknown): TestAnalytics {
  const source = recordOrEmpty(value);
  return {
    summary: toAnalyticsSummary(source['summary']),
    testMetrics: listOf(source['testMetrics'], toTestMetrics),
    questionDifficulty: listOf(source['questionDifficulty'], toQuestionDifficulty),
    trendData: listOf(source['trendData'], toTrendPoint),
  };
}

function toAnalyticsSummary(value: unknown): TestAnalyticsSummary {
  const source = recordOrEmpty(value);
  return {
    totalTests: countOrZero(source['totalTests']),
    totalAttempts: countOrZero(source['totalAttempts']),
    overallPassRate: countOrZero(source['overallPassRate']),
    averageScore: countOrZero(source['averageScore']),
  };
}

function toTestMetrics(value: unknown): TestMetrics {
  const source = recordOrEmpty(value);
  return {
    testId: idString(source['testId']),
    testTitle: nullableString(source['testTitle']) ?? '',
    attempts: countOrZero(source['attempts']),
    passRate: countOrZero(source['passRate']),
    averageScore: countOrZero(source['averageScore']),
    averageTimeSeconds: countOrZero(source['averageTimeSeconds']),
  };
}

function toQuestionDifficulty(value: unknown): QuestionDifficulty {
  const source = recordOrEmpty(value);
  return {
    questionId: idString(source['questionId']),
    testId: idString(source['testId']),
    testTitle: nullableString(source['testTitle']) ?? '',
    questionText: nullableString(source['questionText']) ?? '',
    totalAttempts: countOrZero(source['totalAttempts']),
    incorrectCount: countOrZero(source['incorrectCount']),
    failureRate: countOrZero(source['failureRate']),
  };
}

function toTrendPoint(value: unknown): { date: string; passRate: number; attempts: number } {
  const source = recordOrEmpty(value);
  return { date: nullableString(source['date']) ?? '', passRate: countOrZero(source['passRate']), attempts: countOrZero(source['attempts']) };
}
